import type {
	CreatePropertyViewPayload,
	PropertyViewResponse,
	StartTrackingOptions,
	SyncOfflineViewsPayload,
	SyncOfflineViewsResponse,
	UpdatePropertyViewPayload
} from './types';

import { usePropertyViewTrackingStore } from '~/store/propertyViewTrackingStore';
import { nuxtPropertyViewsRepository } from '~~/adapters/repositories/nuxt';

import {
	clearOfflineViews,
	getOfflineViews,
	getOfflineViewsCount,
	saveViewOffline
} from './offlineStorage';
import { createInitialState, createViewPayload } from './types';

const UPDATE_INTERVAL_MS = 10_000;

export function usePropertyViewTracking() {
	const store = usePropertyViewTrackingStore();
	const { loggedIn } = useUserSession();
	const mixpanel = useMixpanel();
	const getCookieData = useCookieData();

	const getSessionId = () => mixpanel.getAnonymousId() ?? 'unknown';

	function calculateEngagement() {
		if (!store.state) return null;
		const duration = Math.floor((Date.now() - store.state.startTime) / 1000);
		const completionRate =
			store.state.viewType === 'video' && store.state.videoDuration > 0
				? Math.min(store.state.currentTime / store.state.videoDuration, 1)
				: Math.min(duration / 60, 1);

		return {
			duration,
			completionRate: Math.round(completionRate * 100) / 100,
			clicked: store.state.hasClicked,
			favorited: store.state.hasFavorited,
			contacted: store.state.hasContacted
		};
	}

	function createView(payload: CreatePropertyViewPayload): void {
		if (!loggedIn.value) return;
		nuxtPropertyViewsRepository
			.createView(payload, getCookieData())
			.then(([response]) => {
				if (response?._id && store.state?.propertyId === payload.propertyId) {
					store.setViewId(response._id);
				}
			})
			.catch(() => {
				if (store.state?.propertyId === payload.propertyId) {
					saveViewOffline(
						store.state,
						payload.sessionId,
						{},
						store.state.videoData
					);
				}
			});
	}

	function updateView(
		viewId: string,
		payload: UpdatePropertyViewPayload
	): void {
		if (!loggedIn.value || !viewId) return;
		nuxtPropertyViewsRepository
			.updateViewEngagement(viewId, payload, getCookieData())
			.catch(() => {});
	}

	function sendPeriodicUpdate(): void {
		if (!store.state) return;
		const engagement = calculateEngagement();
		// Don't send engagement if completion rate is less than 10%
		if (!engagement || engagement.completionRate < 0.1) return;

		if (loggedIn.value && store.state.viewId) {
			updateView(store.state.viewId, { engagement });
		} else if (!loggedIn.value) {
			saveViewOffline(
				store.state,
				getSessionId(),
				engagement,
				store.state.videoData
			);
		}
	}

	function startTracking(options: StartTrackingOptions): void {
		finishCurrentTracking();
		store.setState(createInitialState(options));

		if (!options.existingViewId) {
			createView(createViewPayload(options, getSessionId()));
		}

		if (!loggedIn.value && store.state) {
			saveViewOffline(store.state, getSessionId(), {}, store.state.videoData);
		}

		const intervalId = setInterval(sendPeriodicUpdate, UPDATE_INTERVAL_MS);
		store.setUpdateIntervalId(intervalId);
	}

	/**
	 * Captures and sends the final engagement for the current tracking session
	 * before clearing state. This avoids a race condition where the new state
	 * could be overwritten by an async catch handler from the old session.
	 */
	function finishCurrentTracking(): void {
		if (!store.state) return;

		const previousState = { ...store.state };
		const engagement = calculateEngagement();
		const shouldSend = engagement && engagement.completionRate >= 0.1;

		store.clearUpdateInterval();
		store.setState(null);

		if (loggedIn.value && previousState.viewId && shouldSend) {
			nuxtPropertyViewsRepository
				.updateViewEngagement(
					previousState.viewId,
					{ engagement },
					getCookieData()
				)
				.catch(() => {});
		} else if (!loggedIn.value && shouldSend) {
			saveViewOffline(
				previousState,
				getSessionId(),
				engagement,
				previousState.videoData
			);
		}
	}

	function stopTracking(): void {
		finishCurrentTracking();
	}

	function pauseTracking(): void {
		store.clearUpdateInterval();
	}

	function resumeTracking(): void {
		if (!store.state || store.updateIntervalId) return;
		const intervalId = setInterval(sendPeriodicUpdate, UPDATE_INTERVAL_MS);
		store.setUpdateIntervalId(intervalId);
	}

	function updateProgress(currentTimeSeconds: number): void {
		store.setCurrentTime(currentTimeSeconds);
	}

	function markEnded(): void {
		if (!store.state || store.state.isEnded) return;
		store.setIsEnded(true);

		if (store.state.viewType === 'video' && store.state.videoDuration > 0) {
			store.setCurrentTime(store.state.videoDuration);
		}

		if (store.state.viewId) {
			const engagement = calculateEngagement();
			// Only send if completion rate is at least 10%
			if (engagement && engagement.completionRate >= 0.1) {
				updateView(store.state.viewId, { engagement });
			}
		}
	}

	function markClicked(): void {
		store.setHasClicked();
	}

	function markFavorited(): void {
		store.setHasFavorited();
	}

	function markContacted(): void {
		store.setHasContacted();
	}

	function getCurrentPropertyId(): string | null {
		return store.state?.propertyId ?? null;
	}

	function isTracking(propertyId?: string): boolean {
		if (!store.state) return false;
		return propertyId ? store.state.propertyId === propertyId : true;
	}

	async function syncOfflineViews(): Promise<SyncOfflineViewsResponse> {
		if (store.isSyncingOffline || !loggedIn.value) {
			return { syncedCount: 0, skippedCount: 0 };
		}

		const views = getOfflineViews();
		if (views.length === 0) {
			return { syncedCount: 0, skippedCount: 0 };
		}

		store.setIsSyncingOffline(true);

		const BATCH_SIZE = 50;
		let totalSynced = 0;
		let totalSkipped = 0;

		try {
			for (let i = 0; i < views.length; i += BATCH_SIZE) {
				const batch = views.slice(i, i + BATCH_SIZE);
				const [response, error] =
					await nuxtPropertyViewsRepository.syncOfflineViews(
						{ views: batch } satisfies SyncOfflineViewsPayload,
						getCookieData()
					);

				if (response) {
					totalSynced += response.syncedCount;
					totalSkipped += response.skippedCount;
				}
			}
			clearOfflineViews();
			return { syncedCount: totalSynced, skippedCount: totalSkipped };
		} catch {
			return {
				syncedCount: totalSynced,
				skippedCount: views.length - totalSynced
			};
		} finally {
			store.setIsSyncingOffline(false);
		}
	}

	return {
		startTracking,
		stopTracking,
		pauseTracking,
		resumeTracking,
		updateProgress,
		markEnded,
		markClicked,
		markFavorited,
		markContacted,
		getCurrentPropertyId,
		isTracking,
		syncOfflineViews,
		getOfflineViewsCount,
		clearOfflineViews
	};
}
